import {
  validateEmail,
  validateNumeric,
} from "../PartnerGenericConfigurationPages/validators/genericConfigValidators";

/**
 * Field definitions registry for each configuration key.
 *
 * Each config key maps to an object describing:
 * - title: drawer header title
 * - description: subtitle / description text
 * - fields: array of field definitions for rendering
 *
 * Field definition shape:
 * {
 *   path: string          — dot-notation path into data (e.g. "joy_points.points")
 *   label: string         — display label
 *   type: string          — "text" | "number" | "email" | "toggle" | "select"
 *   required: boolean     — whether the field is required
 *   readOnly: boolean     — if true, field is always read-only even in edit mode
 *   placeholder: string   — input placeholder
 *   validate: function    — custom validation function (value) => errorString | false
 *   options: array         — for "select" type: [{ label, value }]
 *   section: string       — optional section header to group fields
 * }
 */

export const configFieldSchemas = {
  generic_configuration: {
    title: "Generic Configuration",
    description: "Global configuration for the portal.",
    fields: [
      {
        path: "administrator_destination_email",
        label: "Administrator Destination Email",
        type: "email",
        required: true,
        placeholder: "Enter email address",
        validate: (value) => {
          if (!value || value.trim() === "") return "This field is required";
          return validateEmail(value) || false;
        },
      },
      {
        path: "points_expiration_in_days",
        label: "Points Expiration (Days)",
        type: "number",
        required: true,
        placeholder: "Enter number of days",
        validate: (value) => {
          if (!Number(value)) return "This field is required";
          return validateNumeric(value) || false;
        },
      },
    ],
  },

  partner_configuration: {
    title: "Partner Configuration",
    description: "Setup and configuration details for partners.",
    fields: [
      {
        path: "package_partner",
        label: "Package Partner",
        type: "text",
        required: true,
        placeholder: "Enter package partner",
        validate: (value) =>
          !value || value.trim() === "" ? "This field is required" : false,
      },
      {
        path: "receipt_partner",
        label: "Receipt Partner",
        type: "text",
        required: true,
        placeholder: "Enter receipt partner",
        validate: (value) =>
          !value || value.trim() === "" ? "This field is required" : false,
      },
      {
        path: "generic_notification_partner",
        label: "Generic Notification Partner",
        type: "text",
        required: true,
        placeholder: "Enter generic notification partner",
        validate: (value) =>
          !value || value.trim() === "" ? "This field is required" : false,
      },
    ],
  },

  global_outcomes: {
    title: "Global Outcomes",
    description: "Global configuration for the portal.",
    fields: [
      {
        path: "joy_points.points",
        label: "Points",
        type: "number",
        required: true,
        placeholder: "Enter points value",
        validate: (value) => {
          if (!value || value.toString().trim() === "")
            return "This field is required";
          return false;
        },
      },
      {
        path: "joy_points.limit",
        label: "Limit",
        type: "number",
        required: true,
        placeholder: "Enter limit value",
        validate: (value) => {
          if (!value || value.toString().trim() === "")
            return "This field is required";
          return false;
        },
      },
    ],
  },

  emailPreferences: {
    title: "Transactional Email Notification",
    description: "Events and Keys",
    fields: [
      {
        path: "profileCreation.enabled",
        label: "Profile Created Notification",
        type: "toggle",
      },
      {
        path: "profileCreation.definitionKey",
        label: "Event definition key",
        type: "text",
        placeholder: "Enter definition key",
        dependsOn: "profileCreation.enabled",
        validate: (value, formData) => {
          const enabled = formData?.profileCreation?.enabled;
          if (enabled && (!value || value.toString().trim() === "")) {
            return "Definition key is required when notification is enabled";
          }
          return false;
        },
      },
      {
        path: "profileUpdate.enabled",
        label: "Profile Updated Notification",
        type: "toggle",
      },
      {
        path: "profileUpdate.definitionKey",
        label: "Event definition key",
        type: "text",
        placeholder: "Enter definition key",
        dependsOn: "profileUpdate.enabled",
        validate: (value, formData) => {
          const enabled = formData?.profileUpdate?.enabled;
          if (enabled && (!value || value.toString().trim() === "")) {
            return "Definition key is required when notification is enabled";
          }
          return false;
        },
      },
    ],
  },
};

/**
 * Configs that use JsonForms (schema-driven rendering).
 * These configs fetch their jsonSchema and uiSchema from the API
 * or define them inline, and use JsonFormsWrapper / SummaryFormWrapper.
 */
export const jsonFormsConfigs = {
  user_login_limits: {
    title: "User Limit Settings",
    description: "Configure user login and device limits.",
    configSlug: "user_login_limits",
  },
  app_configurations: {
    title: "App Configurations",
    description: "Configure application settings.",
    configSlug: "app_configurations",
    schemaSlug: "app-configuration",
  },
  social_media_links: {
    title: "Social Media Links",
    description: "Social media link order & configuration.",
    configSlug: "social_media_links",
  },
  widgets_configuration: {
    title: "Widgets Configuration",
    description: "Widgets on Home Screen.",
    configSlug: "widgets_configuration",
    schemaSlug: "widgets-configuration",
  },
};

/**
 * Static configs that don't come from programConfigurations but are
 * injected into the System Settings list. They map their data to
 * another config's key for read/write, and only render when the
 * loadUiComponent API returns schemas (no fallback).
 */
export const staticConfigs = {
  fraud_abuse_controls: {
    title: "Fraud & Abuse Controls",
    description:
      "Sets global limits to detect and prevent fraudulent or abusive participation.",
    dataKey: "user_login_limits",
    noFallback: true,
  },
  points_conversion_rate: {
    title: "Points Conversion Rate",
    description:
      "Configure the conversion between purchase value and awarded points.",
    dataKey: "global_points_configuration",
    noFallback: true,
  },
  points_lifecycle_settings: {
    title: "Points Lifecycle Settings",
    description:
      "Configure how points move through different statuses throughout their lifecycle.",
    dataKey: "global_points_configuration",
    noFallback: true,
  },
};

/**
 * Returns the field definitions as a validators object
 * compatible with useDrawerConfigForm's fieldDefinitions parameter.
 */
export const getFieldValidators = (configKey) => {
  const schema = configFieldSchemas[configKey];
  if (!schema) return {};

  return schema.fields.reduce((acc, field) => {
    const validator = {};
    if (field.required) validator.required = true;
    if (field.validate) validator.validate = field.validate;
    acc[field.path] = validator;
    return acc;
  }, {});
};

/**
 * Returns the list of explicitly registered configuration keys
 * that have drawer support (either field-based or jsonForms-based).
 */
export const getRegisteredConfigKeys = () => {
  return [...Object.keys(configFieldSchemas), ...Object.keys(jsonFormsConfigs)];
};

// Keep backward compat alias
export const getSupportedConfigKeys = getRegisteredConfigKeys;

/**
 * Derives the loadUiComponent slug for a given configuration key.
 * - If the config has an explicit schemaSlug in jsonFormsConfigs, use it.
 * - Otherwise, use the convention: "system_settings_{configKey}".
 *
 * @param {string} configKey
 * @returns {string} slug for loadUiComponent query
 */
export const getSchemaSlug = (configKey) => {
  const explicitSlug = jsonFormsConfigs[configKey]?.schemaSlug;
  return explicitSlug || `system_settings_${configKey}`;
};

/**
 * Auto-generates field definitions from a config data object.
 * Used for configs that are not explicitly registered in configFieldSchemas.
 *
 * Recursively traverses nested objects and creates appropriate field types
 * based on the value types found in the data.
 *
 * @param {Object} data - The config data object
 * @param {string} prefix - Dot-notation prefix for nested paths
 * @returns {Array} Array of field definitions
 */
export const generateFieldsFromData = (data, prefix = "") => {
  if (!data || typeof data !== "object") return [];

  return Object.entries(data).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    const label = key
      .replace(/_/g, " ")
      .replace(/\./g, " › ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      return generateFieldsFromData(value, path);
    }

    if (typeof value === "boolean") {
      return [{ path, label, type: "toggle" }];
    }

    if (
      typeof value === "number" ||
      (!isNaN(Number(value)) && value !== "" && value !== null)
    ) {
      return [
        { path, label, type: "number", placeholder: `Enter ${label.toLowerCase()}` },
      ];
    }

    return [
      { path, label, type: "text", placeholder: `Enter ${label.toLowerCase()}` },
    ];
  });
};
